import React, { useState, useEffect } from 'react';
import {
  Label,
  FormGroup,
  Row,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Button
} from 'reactstrap';
import Car, {
  SUV,
  Truck,
  Trailer,
  Wheeler18DryVan,
  CarHauler,
  EnclosedTrailer,
  Wheeler18FlatBed,
  DumpTrailer,
  MonsterTruck,
  LiftTruck,
  Float53FeetAsGooseneckTrailer,
  SprinterVan
} from '../../../../assets/svgs/CarTypes';
import { normalizeShipmentVehicleTypeForApi } from '../vehicleTypeApi';
import './VehicleTypes.css';

const TRAILER_VEHICLE_TYPES = new Set(['trailer', 'enclosed_trailer', 'dump_trailer', 'gooseneck_trailer', 'car_hauler']);
const UTILITY_TRAILER_MAX_LENGTH_FEET = 14;

const hasValidDimensions = dimensions => {
  if (!dimensions || typeof dimensions !== 'object') {
    return false;
  }

  return ['length', 'width', 'height'].every(key => {
    const numericValue = Number(dimensions[key]);
    return Number.isFinite(numericValue) && numericValue > 0;
  });
};

const VehicleTypes = ({ weight, vehicleType, setVehicleType, trailerDimensions, setTrailerDimensions, trailerDimensionsError }) => {
  const normalizedVehicleType = normalizeShipmentVehicleTypeForApi(vehicleType);
  const [isTruckHover, setIsTruckHover] = useState(false);
  const [isMonsterTruckHover, setIsMonsterTruckHover] = useState(false);
  const [isLiftTruckHover, setIsLiftTruckHover] = useState(false);
  const [is18WheelerDryVanHover, setIs18WheelerDryVanHover] = useState(false);
  const [is18WheelerFlatBedHover, setIs18WheelerFlatBedHover] = useState(false);
  const [isEnclosedTrailerHover, setIsEnclosedTrailerHover] = useState(false);
  const [isDumpTrailerHover, setIsDumpTrailerHover] = useState(false);
  const [isGooseneckTrailerHover, setIsGooseneckTrailerHover] = useState(false);
  const [isCarHaulerHover, setIsCarHaulerHover] = useState(false);
  const [isTrailerHover, setIsTrailerHover] = useState(false);
  const [isSprinterVanHover, setIsSprinterVanHover] = useState(false);

  const [is18WellerOpen, setIs18WellerOpen] = useState(false);
  const [isTruckOpen, setIsTruckOpen] = useState(false);
  const [isTrailerOpen, setIsTrailerOpen] = useState(false);
  const [isTrailerDimensionsModalOpen, setIsTrailerDimensionsModalOpen] = useState(false);
  const [pendingTrailerVehicleType, setPendingTrailerVehicleType] = useState('');
  const [pendingTrailerDropdownClose, setPendingTrailerDropdownClose] = useState(null);
  const [modalDimensions, setModalDimensions] = useState({ length: '', width: '', height: '' });
  const [modalDimensionsError, setModalDimensionsError] = useState('');

  const toggle18Wheeler = () => setIs18WellerOpen(prevState => !prevState);
  const toggleTruck = () => setIsTruckOpen(prevState => !prevState);
  const toggleTrailer = () => setIsTrailerOpen(prevState => !prevState);
  const tooltipTargets = {
    monsterTruck: 'dispatch-vehicle-choice-monster-truck',
    liftTruck: 'dispatch-vehicle-choice-lift-truck',
    truck: 'dispatch-vehicle-choice-truck',
    sprinterVan: 'dispatch-vehicle-choice-sprinter-van',
    enclosedTrailer: 'dispatch-vehicle-choice-enclosed-trailer',
    dumpTrailer: 'dispatch-vehicle-choice-dump-trailer',
    gooseneckTrailer: 'dispatch-vehicle-choice-gooseneck-trailer',
    carHauler: 'dispatch-vehicle-choice-car-hauler',
    trailer: 'dispatch-vehicle-choice-trailer',
    wheelerDryVan: 'dispatch-vehicle-choice-18-wheeler-dry-van',
    wheelerFlatBed: 'dispatch-vehicle-choice-18-wheeler-flat-bed'
  };

  useEffect(() => {
    setIsTruckHover(false);
    setIsMonsterTruckHover(false);
    setIsLiftTruckHover(false);
    setIsEnclosedTrailerHover(false);
    setIsDumpTrailerHover(false);
    setIsGooseneckTrailerHover(false);
    setIsCarHaulerHover(false);
    setIsTrailerHover(false);
    setIsSprinterVanHover(false);
    setIs18WheelerDryVanHover(false);
    setIs18WheelerFlatBedHover(false);
  }, [weight, vehicleType]);

  const handleFill = name => {
    if (name === normalizedVehicleType) {
      return '#f7941d';
    } else {
      return '#5e72e4';
    }
  };

  const handleFillGrayOut = () => '#333333';

  const openTrailerDimensionsModal = (nextVehicleType, closeDropdown) => {
    const initialDimensions = trailerDimensions || { length: '', width: '', height: '' };

    setPendingTrailerVehicleType(nextVehicleType);
    setPendingTrailerDropdownClose(() => closeDropdown);
    setModalDimensions({
      length: initialDimensions.length || '',
      width: initialDimensions.width || '',
      height: initialDimensions.height || ''
    });
    setModalDimensionsError('');
    setIsTrailerDimensionsModalOpen(true);
  };

  const closeTrailerDimensionsModal = () => {
    setIsTrailerDimensionsModalOpen(false);
    setPendingTrailerVehicleType('');
    setPendingTrailerDropdownClose(null);
    setModalDimensionsError('');
  };

  const updateModalDimension = (key, value) => {
    setModalDimensions(previous => ({ ...previous, [key]: value }));
    if (modalDimensionsError) {
      setModalDimensionsError('');
    }
  };

  const handleConfirmTrailerDimensions = () => {
    if (!hasValidDimensions(modalDimensions)) {
      setModalDimensionsError('Length, width, and height are required for trailer shipments.');
      return;
    }

    const nextDimensions = {
      length: Number(modalDimensions.length),
      width: Number(modalDimensions.width),
      height: Number(modalDimensions.height)
    };

    if (typeof setTrailerDimensions === 'function') {
      setTrailerDimensions(nextDimensions);
    }

    const resolvedTrailerVehicleType = pendingTrailerVehicleType === 'trailer' && nextDimensions.length > UTILITY_TRAILER_MAX_LENGTH_FEET
      ? 'gooseneck_trailer'
      : pendingTrailerVehicleType;

    setVehicleType(resolvedTrailerVehicleType);

    if (typeof pendingTrailerDropdownClose === 'function') {
      pendingTrailerDropdownClose();
    }

    closeTrailerDimensionsModal();
  };

  const handleDropdownVehicleSelection = (event, nextVehicleType, closeDropdown) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (TRAILER_VEHICLE_TYPES.has(nextVehicleType)) {
      openTrailerDimensionsModal(nextVehicleType, closeDropdown);
      return;
    }

    setVehicleType(nextVehicleType);

    if (typeof closeDropdown === 'function') {
      closeDropdown();
    }
  };

  const getVehicleOptionClassName = name => (name === normalizedVehicleType ? 'vehicle-option vehicle-option-selected' : 'vehicle-option');
  const getDropdownToggleClassName = names =>
    names.includes(normalizedVehicleType) ? 'dropdown-toggle-vehicle dropdown-toggle-vehicle-selected' : 'dropdown-toggle-vehicle';

  const MonsterTruckDropToggle = () => {
    return (
      <Label className="form-check-label-vehicle">
        <div
          id={tooltipTargets.monsterTruck}
          onClick={event => (weight > 4000 ? null : handleDropdownVehicleSelection(event, 'heavy_duty_truck'))}
          onMouseEnter={() => setIsMonsterTruckHover(true)}
          onMouseLeave={() => setIsMonsterTruckHover(false)}
        >
          <MonsterTruck
            alt="Monster Truck"
            title="Monster Truck"
            name="Monster Truck"
            size={1.5}
            fill={weight > 4000 ? handleFillGrayOut('heavy_duty_truck') : handleFill('heavy_duty_truck')}
          ></MonsterTruck>
        </div>
      </Label>
    );
  };

  const LiftTruckDropToggle = () => {
    return (
      <Label className="form-check-label-vehicle">
        {weight > 10000 ? (
          <div id={tooltipTargets.liftTruck}>
            <LiftTruck alt="Lift Truck" title="Lift Truck" name="Lift Truck" size={1.5} fill={handleFillGrayOut('box_truck')}></LiftTruck>
          </div>
        ) : (
          <div id={tooltipTargets.liftTruck} onClick={event => handleDropdownVehicleSelection(event, 'box_truck')} onMouseEnter={() => setIsLiftTruckHover(true)} onMouseLeave={() => setIsLiftTruckHover(false)}>
            <LiftTruck alt="Lift Truck" title="Lift Truck" name="Lift Truck" size={1.5} fill={handleFill('box_truck')}></LiftTruck>
          </div>
        )}
      </Label>
    );
  };

  const TruckDropToggle = () => {
    return (
      <Label className="form-check-label-vehicle">
        {weight > 2000 ? (
          <div id={tooltipTargets.truck}>
            <Truck name="Truck" fill={handleFillGrayOut('truck')}></Truck>
          </div>
        ) : (
          <div
            id={tooltipTargets.truck}
            onClick={event => handleDropdownVehicleSelection(event, 'truck')}
            onMouseEnter={() => setIsTruckHover(true)}
            onMouseLeave={() => setIsTruckHover(false)}
          >
            <Truck name="Truck" fill={handleFill('truck')}></Truck>
          </div>
        )}
      </Label>
    );
  };

  const SprinterVanDropToggle = () => {
    return (
      <Label className="form-check-label-vehicle">
        {weight > 5000 ? (
          <div id={tooltipTargets.sprinterVan}>
            <SprinterVan alt="Sprinter Van" title="Sprinter Van" name="Sprinter Van" size={1.5} fill={handleFillGrayOut('sprinter_van')}></SprinterVan>
          </div>
        ) : (
          <div id={tooltipTargets.sprinterVan} onClick={event => handleDropdownVehicleSelection(event, 'sprinter_van')} onMouseEnter={() => setIsSprinterVanHover(true)} onMouseLeave={() => setIsSprinterVanHover(false)}>
            <SprinterVan alt="Sprinter Van" title="Sprinter Van" name="Sprinter Van" size={1.5} fill={handleFill('sprinter_van')}></SprinterVan>
          </div>
        )}
      </Label>
    );
  };

  const EnclosedTrailerDropToggle = () => {
    return (
      <Label className="form-check-label-vehicle">
        {weight > 5000 ? (
          <div id={tooltipTargets.enclosedTrailer}>
            <EnclosedTrailer alt="Enclosed Trailer" title="Enclosed Trailer" name="Enclosed Trailer" size={1.5} fill={handleFillGrayOut('enclosed_trailer')}></EnclosedTrailer>
          </div>
        ) : (
          <div
            id={tooltipTargets.enclosedTrailer}
            onClick={event => handleDropdownVehicleSelection(event, 'enclosed_trailer')}
            onMouseEnter={() => setIsEnclosedTrailerHover(true)}
            onMouseLeave={() => setIsEnclosedTrailerHover(false)}
          >
            <EnclosedTrailer alt="Enclosed Trailer" title="Enclosed Trailer" name="Enclosed Trailer" size={1.5} fill={handleFill('enclosed_trailer')}></EnclosedTrailer>
          </div>
        )}
      </Label>
    );
  };

  const DumpTrailerDropToggle = () => {
    return (
      <Label className="form-check-label-vehicle">
        {weight > 10000 ? (
          <div id={tooltipTargets.dumpTrailer}>
            <DumpTrailer alt="Dump Trailer" title="Dump Trailer" name="Dump Trailer" size={1.5} fill={handleFillGrayOut('dump_trailer')}></DumpTrailer>
          </div>
        ) : (
          <div id={tooltipTargets.dumpTrailer} onClick={event => handleDropdownVehicleSelection(event, 'dump_trailer')} onMouseEnter={() => setIsDumpTrailerHover(true)} onMouseLeave={() => setIsDumpTrailerHover(false)}>
            <DumpTrailer alt="Dump Trailer" title="Dump Trailer" name="Dump Trailer" size={1.5} fill={handleFill('dump_trailer')}></DumpTrailer>
          </div>
        )}
      </Label>
    );
  };

  const Float53FeetAsGooseneckTrailerDropToggle = () => {
    return (
      <Label className="form-check-label-vehicle">
        {weight > 15000 ? (
          <div id={tooltipTargets.gooseneckTrailer}>
            <Float53FeetAsGooseneckTrailer
              alt="Gooseneck Trailer"
              title="Gooseneck Trailer"
              name="Gooseneck Trailer"
              size={1.5}
              fill={handleFillGrayOut('gooseneck_trailer')}
            ></Float53FeetAsGooseneckTrailer>
          </div>
        ) : (
          <div
            id={tooltipTargets.gooseneckTrailer}
            onClick={event => handleDropdownVehicleSelection(event, 'gooseneck_trailer')}
            onMouseEnter={() => setIsGooseneckTrailerHover(true)}
            onMouseLeave={() => setIsGooseneckTrailerHover(false)}
          >
            <Float53FeetAsGooseneckTrailer
              alt="Gooseneck Trailer"
              title="Gooseneck Trailer"
              name="Gooseneck Trailer"
              size={1.5}
              fill={handleFill('gooseneck_trailer')}
            ></Float53FeetAsGooseneckTrailer>
          </div>
        )}
      </Label>
    );
  };

  const CarHaulerDropToggle = () => {
    return (
      <Label className="form-check-label-vehicle">
        {weight > 10000 ? (
          <div id={tooltipTargets.carHauler}>
            <CarHauler alt="Car Hauler" title="Car Hauler" name="Car Hauler" size={1.5} fill={handleFillGrayOut('car_hauler')}></CarHauler>
          </div>
        ) : (
          <div id={tooltipTargets.carHauler} onClick={event => handleDropdownVehicleSelection(event, 'car_hauler')} onMouseEnter={() => setIsCarHaulerHover(true)} onMouseLeave={() => setIsCarHaulerHover(false)}>
            <CarHauler alt="Car Hauler" title="Car Hauler" name="Car Hauler" size={1.5} fill={handleFill('car_hauler')}></CarHauler>
          </div>
        )}
      </Label>
    );
  };

  const TrailerDropToggle = () => {
    return (
      <Label className="form-check-label-vehicle">
        {weight > 5000 ? (
          <div id={tooltipTargets.trailer}>
            <Trailer alt="Truck and Trailer" title="Truck and Trailer" name="Truck and Trailer" size={1.5} fill={handleFillGrayOut('trailer')}></Trailer>
          </div>
        ) : (
          <div
            id={tooltipTargets.trailer}
            onClick={event => handleDropdownVehicleSelection(event, 'trailer')}
            onMouseEnter={() => setIsTrailerHover(true)}
            onMouseLeave={() => setIsTrailerHover(false)}
          >
            <Trailer alt="Truck and Trailer" title="Truck and Trailer" name="Truck and Trailer" size={1.5} fill={handleFill('trailer')}></Trailer>
          </div>
        )}
      </Label>
    );
  };

  const Wheeler18DryVanDropToggle = () => {
    return (
      <Label className="form-check-label-vehicle">
        <div id={tooltipTargets.wheelerDryVan} onClick={event => handleDropdownVehicleSelection(event, '18_wheeler_dry_van')} onMouseEnter={() => setIs18WheelerDryVanHover(true)} onMouseLeave={() => setIs18WheelerDryVanHover(false)}>
          <Wheeler18DryVan alt="18 wheeler dry van" title="18 wheeler dry van" name="18 wheeler dry van" size={1.5} fill={handleFill('18_wheeler_dry_van')}></Wheeler18DryVan>
        </div>
      </Label>
    );
  };

  const Wheeler18FlatBedDropToggle = () => {
    return (
      <Label className="form-check-label-vehicle">
        <div
          id={tooltipTargets.wheelerFlatBed}
          onClick={event => handleDropdownVehicleSelection(event, '18_wheeler_flat_bed')}
          onMouseEnter={() => setIs18WheelerFlatBedHover(true)}
          onMouseLeave={() => setIs18WheelerFlatBedHover(false)}
        >
          <Wheeler18FlatBed alt="18 wheeler flat bed" title="18 wheeler flat bed" name="18 wheeler flat bed" size={1.5} fill={handleFill('18_wheeler_flat_bed')}></Wheeler18FlatBed>
        </div>
      </Label>
    );
  };

  const TruckDropItem = () => {
    return (
      <Label className="form-check-label-vehicle">
        {weight > 2000 ? (
          <div>
            <Truck name="Truck" fill={handleFillGrayOut('truck')}></Truck>
          </div>
        ) : (
          <div
            onClick={event => handleDropdownVehicleSelection(event, 'truck', toggleTruck)}
            onMouseEnter={() => setIsTruckHover(true)}
            onMouseLeave={() => setIsTruckHover(false)}
          >
            <Truck name="Truck" fill={handleFill('truck')}></Truck>
          </div>
        )}
        <span style={{ marginLeft: '10px' }} />
        {isTruckHover && '1/2 Ton Pickup'}
      </Label>
    );
  };

  const MonsterTruckDropItem = () => {
    return (
      <Label className="form-check-label-vehicle">
        {weight > 4000 ? (
          <div>
            <MonsterTruck alt="Monster Truck" title="Monster Truck" name="Monster Truck" size={1.5} fill={handleFillGrayOut('heavy_duty_truck')}></MonsterTruck>
          </div>
        ) : (
          <div
            onClick={event => handleDropdownVehicleSelection(event, 'heavy_duty_truck', toggleTruck)}
            onMouseEnter={() => setIsMonsterTruckHover(true)}
            onMouseLeave={() => setIsMonsterTruckHover(false)}
          >
            <MonsterTruck alt="Monster Truck" title="Monster Truck" name="Monster Truck" size={1.5} fill={handleFill('heavy_duty_truck')}></MonsterTruck>
          </div>
        )}
        <span style={{ marginLeft: '10px' }} />
        {isMonsterTruckHover && '1 Ton Pickup'}
      </Label>
    );
  };

  const SprinterVanDropItem = () => {
    return (
      <Label className="form-check-label-vehicle">
        {weight > 5000 ? (
          <div>
            <SprinterVan alt="Sprinter Van" title="Sprinter Van" name="Sprinter Van" size={1.5} fill={handleFillGrayOut('sprinter_van')}></SprinterVan>
          </div>
        ) : (
          <div
            onClick={event => handleDropdownVehicleSelection(event, 'sprinter_van', toggleTruck)}
            onMouseEnter={() => setIsSprinterVanHover(true)}
            onMouseLeave={() => setIsSprinterVanHover(false)}
          >
            <SprinterVan alt="Sprinter Van" title="Sprinter Van" name="Sprinter Van" size={1.5} fill={handleFill('sprinter_van')}></SprinterVan>
          </div>
        )}
        <span style={{ marginLeft: '10px' }} />
        {isSprinterVanHover && 'Sprinter Van'}
      </Label>
    );
  };

  const LiftTruckDropItem = () => {
    return (
      <Label className="form-check-label-vehicle">
        {weight > 10000 ? (
          <div>
            <LiftTruck alt="Lift Truck" title="Lift Truck" name="Lift Truck" size={1.5} fill={handleFillGrayOut('box_truck')}></LiftTruck>
          </div>
        ) : (
          <div
            onClick={event => handleDropdownVehicleSelection(event, 'box_truck', toggleTruck)}
            onMouseEnter={() => setIsLiftTruckHover(true)}
            onMouseLeave={() => setIsLiftTruckHover(false)}
          >
            <LiftTruck alt="Lift Truck" title="Lift Truck" name="Lift Truck" size={1.5} fill={handleFill('box_truck')}></LiftTruck>
          </div>
        )}
        <span style={{ marginLeft: '10px' }} />
        {isLiftTruckHover && 'Box Truck'}
      </Label>
    );
  };

  const TrailerDropItem = () => {
    return (
      <Label className="form-check-label-vehicle">
        {weight > 5000 ? (
          <div>
            <Trailer alt="Truck and Trailer" title="Truck and Trailer" name="Truck and Trailer" size={1.5} fill={handleFillGrayOut('trailer')}></Trailer>
          </div>
        ) : (
          <div
            onClick={event => handleDropdownVehicleSelection(event, 'trailer', toggleTrailer)}
            onMouseEnter={() => setIsTrailerHover(true)}
            onMouseLeave={() => setIsTrailerHover(false)}
          >
            <Trailer alt="Truck and Trailer" title="Truck and Trailer" name="Truck and Trailer" size={1.5} fill={handleFill('trailer')}></Trailer>
          </div>
        )}
        <span style={{ marginLeft: '5px', marginTop: '5px' }} />
        {isTrailerHover && 'Utility Trailer'}
      </Label>
    );
  };

  const EnclosedTrailerDropItem = () => {
    return (
      <Label className="form-check-label-vehicle">
        {weight > 5000 ? (
          <div>
            <EnclosedTrailer alt="Enclosed Trailer" title="Enclosed Trailer" name="Enclosed Trailer" size={1.5} fill={handleFillGrayOut('enclosed_trailer')}></EnclosedTrailer>
          </div>
        ) : (
          <div
            onClick={event => handleDropdownVehicleSelection(event, 'enclosed_trailer', toggleTrailer)}
            onMouseEnter={() => setIsEnclosedTrailerHover(true)}
            onMouseLeave={() => setIsEnclosedTrailerHover(false)}
          >
            <EnclosedTrailer alt="Enclosed Trailer" title="Enclosed Trailer" name="Enclosed Trailer" size={1.5} fill={handleFill('enclosed_trailer')}></EnclosedTrailer>
          </div>
        )}
        <span style={{ marginLeft: '5px', marginTop: '10px' }} />
        {isEnclosedTrailerHover && 'Enclosed Trailer'}
      </Label>
    );
  };

  const DumpTrailerDropItem = () => {
    return (
      <Label className="form-check-label-vehicle">
        {weight > 10000 ? (
          <div>
            <DumpTrailer alt="Dump Trailer" title="Dump Trailer" name="Dump Trailer" size={1.5} fill={handleFillGrayOut('dump_trailer')}></DumpTrailer>
          </div>
        ) : (
          <div
            onClick={event => handleDropdownVehicleSelection(event, 'dump_trailer', toggleTrailer)}
            onMouseEnter={() => setIsDumpTrailerHover(true)}
            onMouseLeave={() => setIsDumpTrailerHover(false)}
          >
            <DumpTrailer alt="Dump Trailer" title="Dump Trailer" name="Dump Trailer" size={1.5} fill={handleFill('dump_trailer')}></DumpTrailer>
          </div>
        )}
        <span style={{ marginLeft: '5px', marginTop: '10px' }} />
        {isDumpTrailerHover && 'Dump Trailer'}
      </Label>
    );
  };

  const Float53FeetAsGooseneckTrailerDropItem = () => {
    return (
      <Label className="form-check-label-vehicle">
        {weight > 15000 ? (
          <div>
            <Float53FeetAsGooseneckTrailer
              alt="Gooseneck Trailer"
              title="Gooseneck Trailer"
              name="Gooseneck Trailer"
              size={1.5}
              fill={handleFillGrayOut('gooseneck_trailer')}
            ></Float53FeetAsGooseneckTrailer>
          </div>
        ) : (
          <div
            onClick={event => handleDropdownVehicleSelection(event, 'gooseneck_trailer', toggleTrailer)}
            onMouseEnter={() => setIsGooseneckTrailerHover(true)}
            onMouseLeave={() => setIsGooseneckTrailerHover(false)}
          >
            <Float53FeetAsGooseneckTrailer
              alt="Gooseneck Trailer"
              title="Gooseneck Trailer"
              name="Gooseneck Trailer"
              size={1.5}
              fill={handleFill('gooseneck_trailer')}
            ></Float53FeetAsGooseneckTrailer>
          </div>
        )}
        <span style={{ marginLeft: '5px', marginTop: '10px' }} />
        {isGooseneckTrailerHover && 'Gooseneck Trailer'}
      </Label>
    );
  };

  const CarHaulerDropItem = () => {
    return (
      <Label className="form-check-label-vehicle">
        {weight > 10000 ? (
          <div>
            <CarHauler alt="Car Hauler" title="Car Hauler" name="Car Hauler" size={1.5} fill={handleFillGrayOut('car_hauler')}></CarHauler>
          </div>
        ) : (
          <div
            onClick={event => handleDropdownVehicleSelection(event, 'car_hauler', toggleTrailer)}
            onMouseEnter={() => setIsCarHaulerHover(true)}
            onMouseLeave={() => setIsCarHaulerHover(false)}
          >
            <CarHauler alt="Car Hauler" title="Car Hauler" name="Car Hauler" size={1.5} fill={handleFill('car_hauler')}></CarHauler>
          </div>
        )}
        <span style={{ marginLeft: '5px', marginTop: '10px' }} />
        {isCarHaulerHover && 'Car Hauler'}
      </Label>
    );
  };

  const Wheeler18FlatBedDropItem = () => {
    return (
      <Label className="form-check-label-vehicle">
        <div
          onClick={event => handleDropdownVehicleSelection(event, '18_wheeler_flat_bed', toggle18Wheeler)}
          onMouseEnter={() => setIs18WheelerFlatBedHover(true)}
          onMouseLeave={() => setIs18WheelerFlatBedHover(false)}
        >
          <Wheeler18FlatBed alt="18 wheeler flat bed" title="18 wheeler flat bed" name="18 wheeler flat bed" size={1.5} fill={handleFill('18_wheeler_flat_bed')}></Wheeler18FlatBed>
        </div>
        <span style={{ marginTop: '15px' }} />
        {is18WheelerFlatBedHover && '18 Wheeler Flat Bed'}
      </Label>
    );
  };

  const Wheeler18DryVanDropItem = () => {
    return (
      <Label className="form-check-label-vehicle">
        <div
          onClick={event => handleDropdownVehicleSelection(event, '18_wheeler_dry_van', toggle18Wheeler)}
          onMouseEnter={() => setIs18WheelerDryVanHover(true)}
          onMouseLeave={() => setIs18WheelerDryVanHover(false)}
        >
          <Wheeler18DryVan alt="18 wheeler dry van" title="18 wheeler dry van" name="18 wheeler dry van" size={1.5} fill={handleFill('18_wheeler_dry_van')}></Wheeler18DryVan>
        </div>
        <span style={{ marginTop: '15px' }} />
        {is18WheelerDryVanHover && '18 Wheeler Dry Van'}
      </Label>
    );
  };
  return (
    <>
      <Label for="shipment-size" style={{ marginLeft: '10px', display: 'inline-block' }}>
        Shipment Size (click <span role="img" aria-label="down arrow">🔽</span> for more options)
      </Label>
      {trailerDimensionsError && (
        <p style={{ color: 'red', marginLeft: '10px', marginBottom: '5px' }}>Trailer dimensions are required when selecting a trailer type.</p>
      )}
      <Row style={{ textAlign: 'center' }}>
        <div className="vehicle-selection-shell">
          <FormGroup check className="form-check-radio vehicle-selection-row" id="shipment-size">
            <Label className="form-check-label vehicle-selection-item">
              {weight > 500 ? (
                <div className={getVehicleOptionClassName('car')}>
                  <Car name="Car" fill={handleFillGrayOut('car')}></Car>
                </div>
              ) : (
                <div className={getVehicleOptionClassName('car')} onClick={() => setVehicleType('car')}>
                  <Car name="Car" fill={handleFill('car')}></Car>
                </div>
              )}
            </Label>

            <Label className="form-check-label vehicle-selection-item">
              {weight > 1000 ? (
                <div className={getVehicleOptionClassName('suv')}>
                  <SUV name="SUV" fill={handleFillGrayOut('suv')}></SUV>
                </div>
              ) : (
                <div className={getVehicleOptionClassName('suv')} onClick={() => setVehicleType('suv')}>
                  <SUV name="SUV" fill={handleFill('suv')}></SUV>
                </div>
              )}
            </Label>

            <Label className="form-check-label vehicle-selection-item" style={{ paddingRight: '10px', marginRight: '10px' }}>
              <UncontrolledDropdown className="dropdown-vehicle" isOpen={isTruckOpen} toggle={toggleTruck}>
                <DropdownToggle className={getDropdownToggleClassName(['pickup_truck', 'truck', 'heavy_duty_truck', 'box_truck', 'sprinter_van'])} caret tag="button" type="button">
                  {normalizedVehicleType === 'pickup_truck' || normalizedVehicleType === 'truck' ? (
                    <TruckDropToggle />
                  ) : normalizedVehicleType === 'heavy_duty_truck' ? (
                    <MonsterTruckDropToggle />
                  ) : normalizedVehicleType === 'box_truck' ? (
                    <LiftTruckDropToggle />
                  ) : normalizedVehicleType === 'sprinter_van' ? (
                    <SprinterVanDropToggle />
                  ) : weight < 2000 ? (
                    <TruckDropToggle />
                  ) : weight < 4000 ? (
                    <MonsterTruckDropToggle />
                  ) : weight < 5000 ? (
                    <SprinterVanDropToggle />
                  ) : weight < 10000 ? (
                    <LiftTruckDropToggle />
                  ) : (
                    <TruckDropToggle />
                  )}
                  🔽
                </DropdownToggle>
                <DropdownMenu>
                  {(weight < 2000 || weight > 10000) && (
                    <>
                      {(vehicleType === 'pickup_truck' || vehicleType === 'heavy_duty_truck' || vehicleType === 'truck' || vehicleType === 'box_truck' || vehicleType === 'sprinter_van') && (
                        <DropdownItem className="dropdown-item-vehicle" text>
                          <TruckDropItem />
                        </DropdownItem>
                      )}
                      {vehicleType !== 'heavy_duty_truck' && (
                        <DropdownItem className="dropdown-item-vehicle" text>
                          <MonsterTruckDropItem />
                        </DropdownItem>
                      )}
                      {vehicleType !== 'sprinter_van' && (
                        <DropdownItem className="dropdown-item-vehicle" text>
                          <SprinterVanDropItem />
                        </DropdownItem>
                      )}
                      {vehicleType !== 'box_truck' && (
                        <DropdownItem className="dropdown-item-vehicle" text>
                          <LiftTruckDropItem />
                        </DropdownItem>
                      )}
                    </>
                  )}
                  {weight >= 2000 && weight < 4000 && (
                    <>
                      <DropdownItem className="dropdown-item-vehicle" text>
                        <TruckDropItem />
                      </DropdownItem>

                      <DropdownItem className="dropdown-item-vehicle" text>
                        <SprinterVanDropItem />
                      </DropdownItem>

                      {vehicleType === 'box_truck' && (
                        <DropdownItem className="dropdown-item-vehicle" text>
                          <MonsterTruckDropItem />
                        </DropdownItem>
                      )}
                      {vehicleType !== 'box_truck' && (
                        <DropdownItem className="dropdown-item-vehicle" text>
                          <LiftTruckDropItem />
                        </DropdownItem>
                      )}
                    </>
                  )}
                  {weight >= 4000 && weight < 10000 && (
                    <>
                      <DropdownItem className="dropdown-item-vehicle" text>
                        <TruckDropItem />
                      </DropdownItem>
                      <DropdownItem className="dropdown-item-vehicle" text>
                        <MonsterTruckDropItem />
                      </DropdownItem>
                      <DropdownItem className="dropdown-item-vehicle" text>
                        <SprinterVanDropItem />
                      </DropdownItem>
                    </>
                  )}
                </DropdownMenu>
              </UncontrolledDropdown>
            </Label>

            <Label className="form-check-label-vehicle-dropdown vehicle-selection-item">
              <UncontrolledDropdown className="dropdown-vehicle" isOpen={isTrailerOpen} toggle={toggleTrailer}>
                <DropdownToggle className={getDropdownToggleClassName(['trailer', 'enclosed_trailer', 'dump_trailer', 'gooseneck_trailer', 'car_hauler'])} caret tag="button" type="button">
                  {normalizedVehicleType === 'enclosed_trailer' ? (
                    <EnclosedTrailerDropToggle />
                  ) : normalizedVehicleType === 'dump_trailer' ? (
                    <DumpTrailerDropToggle />
                  ) : normalizedVehicleType === 'gooseneck_trailer' ? (
                    <Float53FeetAsGooseneckTrailerDropToggle />
                  ) : normalizedVehicleType === 'car_hauler' ? (
                    <CarHaulerDropToggle />
                  ) : weight < 5000 ? (
                    <TrailerDropToggle />
                  ) : weight < 10000 ? (
                    <DumpTrailerDropToggle />
                  ) : weight < 15000 ? (
                    <Float53FeetAsGooseneckTrailerDropToggle />
                  ) : (
                    <TrailerDropToggle />
                  )}
                  🔽
                </DropdownToggle>
                <DropdownMenu>
                  {(weight < 5000 || weight > 18000) && (
                    <>
                      {(vehicleType === 'enclosed_trailer' || vehicleType === 'dump_trailer' || vehicleType === 'gooseneck_trailer' || vehicleType === 'car_hauler') && (
                        <DropdownItem className="dropdown-item-vehicle" text>
                          <TrailerDropItem />
                        </DropdownItem>
                      )}
                      {vehicleType !== 'enclosed_trailer' && (
                        <DropdownItem className="dropdown-item-vehicle" text>
                          <EnclosedTrailerDropItem />
                        </DropdownItem>
                      )}
                      {vehicleType !== 'dump_trailer' && (
                        <DropdownItem className="dropdown-item-vehicle" text>
                          <DumpTrailerDropItem />
                        </DropdownItem>
                      )}

                      {vehicleType !== 'car_hauler' && (
                        <DropdownItem className="dropdown-item-vehicle" text>
                          <CarHaulerDropItem />
                        </DropdownItem>
                      )}
                      {vehicleType !== 'gooseneck_trailer' && (
                        <DropdownItem className="dropdown-item-vehicle" text>
                          <Float53FeetAsGooseneckTrailerDropItem />
                        </DropdownItem>
                      )}
                    </>
                  )}
                  {weight > 5000 && weight < 10000 && (
                    <>
                      <DropdownItem className="dropdown-item-vehicle" text>
                        <TrailerDropItem />
                      </DropdownItem>
                      <DropdownItem className="dropdown-item-vehicle" text>
                        <EnclosedTrailerDropItem />
                      </DropdownItem>

                      {vehicleType === '' ? null : vehicleType === 'car_hauler' || vehicleType === 'gooseneck_trailer' ? (
                        <DropdownItem className="dropdown-item-vehicle" text>
                          <DumpTrailerDropItem />
                        </DropdownItem>
                      ) : null}

                      {/* {vehicleType !== 'dump_trailer' && (
                        <DropdownItem className="dropdown-item-vehicle" text>
                          <DumpTrailerDropItem />
                        </DropdownItem>
                      )} */}
                      {vehicleType !== 'car_hauler' && (
                        <DropdownItem className="dropdown-item-vehicle" text>
                          <CarHaulerDropItem />
                        </DropdownItem>
                      )}

                      {vehicleType !== 'gooseneck_trailer' && (
                        <DropdownItem className="dropdown-item-vehicle" text>
                          <Float53FeetAsGooseneckTrailerDropItem />
                        </DropdownItem>
                      )}
                    </>
                  )}
                  {weight > 10000 && weight < 18000 && (
                    <>
                      <DropdownItem className="dropdown-item-vehicle" text>
                        <TrailerDropItem />
                      </DropdownItem>
                      <DropdownItem className="dropdown-item-vehicle" text>
                        <EnclosedTrailerDropItem />
                      </DropdownItem>
                      <DropdownItem className="dropdown-item-vehicle" text>
                        <DumpTrailerDropItem />
                      </DropdownItem>
                      <DropdownItem className="dropdown-item-vehicle" text>
                        <CarHaulerDropItem />
                      </DropdownItem>
                    </>
                  )}
                </DropdownMenu>
              </UncontrolledDropdown>
            </Label>
            <Label className="form-check-label-vehicle-dropdown vehicle-selection-item">
              <UncontrolledDropdown className="dropdown-vehicle" isOpen={is18WellerOpen} toggle={toggle18Wheeler}>
                <DropdownToggle className={getDropdownToggleClassName(['18_wheeler_dry_van', '18_wheeler_flat_bed'])} caret tag="button" type="button">
                  {normalizedVehicleType === '18_wheeler_dry_van' ? <Wheeler18DryVanDropToggle /> : <Wheeler18FlatBedDropToggle />}
                  🔽
                </DropdownToggle>
                <DropdownMenu>
                  {vehicleType === '18_wheeler_dry_van' && (
                    <DropdownItem className="dropdown-item-vehicle" text>
                      <Wheeler18FlatBedDropItem />
                    </DropdownItem>
                  )}
                  {vehicleType !== '18_wheeler_dry_van' && (
                    <DropdownItem className="dropdown-item-vehicle" text>
                      <Wheeler18DryVanDropItem />
                    </DropdownItem>
                  )}
                </DropdownMenu>
              </UncontrolledDropdown>
            </Label>
          </FormGroup>
        </div>
      </Row>
      <Modal isOpen={isTrailerDimensionsModalOpen} toggle={closeTrailerDimensionsModal}>
        <ModalHeader toggle={closeTrailerDimensionsModal}>Trailer Dimensions Required</ModalHeader>
        <ModalBody>
          {pendingTrailerVehicleType === 'trailer' && (
            <p>
              Utility trailer is for loads less than 5000 lbs and up to 14 ft in length. Lengths above 14 ft will be
              automatically switched to gooseneck trailer.
            </p>
          )}
          <Row form>
            <FormGroup style={{ width: '100%', marginBottom: '10px' }}>
              <Label for="trailer-length">Length (ft)</Label>
              <Input
                id="trailer-length"
                type="number"
                min="0"
                step="0.1"
                value={modalDimensions.length}
                onChange={event => updateModalDimension('length', event.target.value)}
                placeholder="Enter length"
                style={{ color: '#212529', WebkitTextFillColor: '#212529' }}
              />
            </FormGroup>
            <FormGroup style={{ width: '100%', marginBottom: '10px' }}>
              <Label for="trailer-width">Width (ft)</Label>
              <Input
                id="trailer-width"
                type="number"
                min="0"
                step="0.1"
                value={modalDimensions.width}
                onChange={event => updateModalDimension('width', event.target.value)}
                placeholder="Enter width"
                style={{ color: '#212529', WebkitTextFillColor: '#212529' }}
              />
            </FormGroup>
            <FormGroup style={{ width: '100%', marginBottom: 0 }}>
              <Label for="trailer-height">Height (ft)</Label>
              <Input
                id="trailer-height"
                type="number"
                min="0"
                step="0.1"
                value={modalDimensions.height}
                onChange={event => updateModalDimension('height', event.target.value)}
                placeholder="Enter height"
                style={{ color: '#212529', WebkitTextFillColor: '#212529' }}
              />
            </FormGroup>
          </Row>
          {modalDimensionsError && <p style={{ color: 'red', marginTop: '10px', marginBottom: 0 }}>{modalDimensionsError}</p>}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeTrailerDimensionsModal}>Cancel</Button>
          <Button color="primary" onClick={handleConfirmTrailerDimensions}>Save and Select Trailer</Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default VehicleTypes;
